import type { FunctionComponent } from "react";

import { useMemo, useState } from "react";
import { useSelector } from "../../hooks/redux";
import { parseISO } from "date-fns";
import {
  Box,
  Grid,
  Menu,
  MenuItem,
  Paper,
  TableRow,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import TooltipButton from "../TooltipButton";
import AlertDialog from "../AlertDialog";
import NoteField from "./NoteField";
import {
  DeleteParticipantNoteData,
  UpdateParticipantNoteData,
} from "../../REST/notesRequestsDefinitions";
import { useDateFormatter, useHttpRequest, useSnackbar } from "../../hooks";
import * as template from "./template";

enum SelectionChoiceCommands {
  EDIT = "edit",
  DELETE = "delete",
}

const noteModificationOptions = [
  {
    action: SelectionChoiceCommands.EDIT,
    text: template.viewNote.selections.edit,
  },
  {
    action: SelectionChoiceCommands.DELETE,
    text: template.viewNote.selections.delete,
  },
];

const useStyles = makeStyles((_theme) => ({
  container: {
    marginRight: "5px",
    marginTop: "5px",
    maxWidth: "100%",
  },
  contents: {
    marginTop: "0.5vh",
    paddingBottom: "0.5vh",
    paddingLeft: "0.5vw",
    paddingRight: "0.5vw",
    height: "fit-content",
    maxHeight: "65px",
    overflowY: "auto",
    width: "100%",
  },
  contentsMini: {
    marginTop: "0.5vh",
    paddingBottom: "0.5vh",
    paddingLeft: "0.5vw",
    paddingRight: "0.5vw",
    height: "fit-content",
    maxHeight: "55px",
    overflowY: "auto",
  },
  textDisplay: {
    overflowWrap: "anywhere",
  },
}));

export interface ParticipantNoteItem {
  guid: number;
  author: string;
  editor?: string;
  content: string;
  createdAt: string;
  modifiedAt: string;
}

interface Props {
  mini?: boolean;
  noteBeingModified: number | null;
  participantNote: ParticipantNoteItem;
  participantGuid: string;
  refetch: () => void;
  setNoteBeingModified: any;
}

const ViewNote: FunctionComponent<Props> = (props) => {
  const {
    mini = false,
    noteBeingModified = null,
    participantNote,
    participantGuid,
    refetch,
  } = props;
  const classes = useStyles();
  const {
    guid,
    author,
    editor = undefined,
    content,
    createdAt,
    modifiedAt,
  } = participantNote;

  const { accountGuid, productionGuid } = useSelector(
    (state) => state.queue.showInfo
  );
  const pageView = useSelector((state) => state.queue.page);
  const userInfo = useSelector((state) => state.queue.userInfo);

  const { enqueueSnackbar } = useSnackbar();

  const formatDate = useDateFormatter();

  const [deleteRequest] = useHttpRequest<DeleteParticipantNoteData>(
    `admin_proxy/participants/${participantGuid}/notes/${guid}`,
    {
      method: "delete",
      searchParams: {
        accountGuid,
        productionGuid,
      },
      onSuccess: () => {
        enqueueSnackbar(template.viewNote.snackbars.noteRemoved, {
          variant: "info",
        });

        refetch();
      },
    }
  );

  const [updateRequest] = useHttpRequest<
    EmptyObject,
    UpdateParticipantNoteData
  >(`admin_proxy/participants/${participantGuid}/notes/${guid}`, {
    method: "put",
    body: {
      accountGuid,
      productionGuid,
    },
    onSuccess: () => {
      enqueueSnackbar(template.viewNote.snackbars.noteUpdated, {
        variant: "info",
      });

      refetch();
    },
  });

  const { displayName: currentUser } = userInfo;

  const [noteModificationDisabled, setNoteModificationDisabled] =
    useState<boolean>(Boolean(noteBeingModified) /*currentUserIsAuthor*/);

  const formattedNoteCreated = formatDate(parseISO(createdAt));

  const formattedNoteEdited = formatDate(parseISO(modifiedAt));

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [displayDeletionConfirmation, setDisplayDeletionConfirmation] =
    useState<boolean>(false);

  function handleSelect(choice: SelectionChoiceCommands): void {
    switch (choice) {
      case SelectionChoiceCommands.EDIT:
        props.setNoteBeingModified(guid, false);
        setIsEditing(true);
        setDisplayDeletionConfirmation(false);
        break;
      case SelectionChoiceCommands.DELETE:
        props.setNoteBeingModified(guid, false);
        setIsEditing(false);
        setDisplayDeletionConfirmation(true);
        break;
      default:
        break;
    }

    handleClose();
  }

  function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
    setAnchorEl(event.currentTarget);
  }

  function handleClose(): void {
    setAnchorEl(null);
  }

  function handleCloseDeletionAlert(): void {
    props.setNoteBeingModified(null, false);
    setDisplayDeletionConfirmation(false);
  }

  function handleEditingClose(newContents: string): void {
    if (isEditing) {
      setIsEditing(false);
      if (newContents.length === 0) {
        setDisplayDeletionConfirmation(true);
      } else {
        props.setNoteBeingModified(null, false);
      }
    }
  }

  const handleEditingSave = async (newContents: string) => {
    if (newContents !== content) {
      if (newContents.length === 0) {
        setIsEditing(false);
        setDisplayDeletionConfirmation(true);
      } else {
        props.setNoteBeingModified(guid, true);
        await sendUpdatedNote(newContents);
      }
    }
  };

  useMemo(() => {
    if (noteBeingModified === null) {
      setNoteModificationDisabled(false);
    } else {
      setNoteModificationDisabled(true);
    }
  }, [noteBeingModified]);

  const sendDeleteNote = async () => {
    await deleteRequest({
      searchParams: {
        id: guid,
        participant: participantGuid,
        user: currentUser,
      },
    });

    handleCloseDeletionAlert();
  };

  const sendUpdatedNote = async (newContents: string) => {
    await updateRequest({
      body: {
        user: currentUser,
        content: newContents,
      },
    });

    props.setNoteBeingModified(null, false);
    setIsEditing(false);
  };

  const noteModificationTooltip = noteModificationDisabled
    ? template.viewNote.tooltips.notOkToModify
    : template.viewNote.tooltips.okToModify;

  return (
    <TableRow>
      <Paper className={classes.container} variant="outlined">
        <Grid className="fullWidth" container direction="column">
          <Grid
            className={`${
              mini ? classes.contentsMini : classes.contents
            } fullWidth`}
            id="testy-mcfloobson"
            item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Grid item>
              <Typography gutterBottom variant="subtitle1">
                <b>{`${
                  editor ? formattedNoteEdited : formattedNoteCreated
                }`}</b>{" "}
                - {editor ? editor : author} {editor ? "[edited]" : ""}
              </Typography>
            </Grid>
            <Grid item>
              <TooltipButton
                color={"primary"}
                icon={["fas", "ellipsis-h"]}
                isDisabled={noteModificationDisabled}
                size="small"
                title={noteModificationTooltip}
                onClick={handleClick}
              />
              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                open={Boolean(anchorEl)}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                onClose={handleClose}
              >
                {noteModificationOptions.map((option, i) => {
                  const { action, text } = option;
                  return (
                    <MenuItem key={i} onClick={() => handleSelect(action)}>
                      {text}
                    </MenuItem>
                  );
                })}
              </Menu>
            </Grid>
          </Grid>
          <Grid className={mini ? classes.contentsMini : classes.contents} item>
            {!isEditing && (
              <>
                <Grid item>
                  <Typography className={classes.textDisplay}>
                    {content}
                  </Typography>
                </Grid>

                {editor && (
                  <Grid item>
                    <Typography gutterBottom variant="caption">
                      <i>{`Created: ${formattedNoteCreated} - ${author}`}</i>
                    </Typography>
                  </Grid>
                )}
              </>
            )}
            {isEditing && (
              <NoteField
                contents={content}
                mini={mini}
                onClose={handleEditingClose}
                onSave={handleEditingSave}
              />
            )}
          </Grid>
        </Grid>
      </Paper>
      <AlertDialog
        actions={[
          {
            label: template.viewNote.deleteNoteDialog.confirm,
            onClick: sendDeleteNote,
          },
        ]}
        children={null}
        dialogText={template.viewNote.deleteNoteDialog.text}
        dialogTitle={template.viewNote.deleteNoteDialog.title}
        disableBackdropClick={true}
        handleClose={handleCloseDeletionAlert}
        isOpen={displayDeletionConfirmation}
      />
    </TableRow>
  );
};

export default ViewNote;
